import type { MessageId } from "@redotech/effect-api-shared/ids/definitions";
import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { Message } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { UserImage, UserImageSize } from "@redotech/redo-web/user-image";
import { memo } from "react";

export interface FormattedInternalNote {
  id: MessageId;
  senderName: string;
  content: string;
  htmlContent: string | null;
  timestamp: Date;
  formattedTime: string;
}

export type InternalNoteMessage = Extract<Message, { channel: "internalNote" }>;

export function isInternalNoteMessage(
  message: Message,
): message is InternalNoteMessage {
  return message.channel === "internalNote";
}

export function formatInternalNote(
  message: InternalNoteMessage,
  userNameMap?: Map<string, string>,
): FormattedInternalNote {
  const htmlContent = message.channelData?.htmlContent ?? null;

  let senderName = "Agent";
  if (message.senderMongoUserId && userNameMap) {
    const hexId = MongoUserId.toObjectId(
      message.senderMongoUserId,
    ).toHexString();
    senderName = userNameMap.get(hexId) ?? "Agent";
  }

  return {
    id: message.id,
    senderName,
    content: message.content ?? "",
    htmlContent,
    timestamp: message.sentAt,
    formattedTime: formatNoteTime(message.sentAt),
  };
}

function formatNoteTime(date: Date): string {
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  const isThisYear = date.getFullYear() === now.getFullYear();
  if (isThisYear) {
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export const InternalNoteCard = memo(function InternalNoteCard({
  note,
  isLast,
}: {
  note: FormattedInternalNote;
  isLast?: boolean;
}) {
  return (
    <Flex
      borderBottomWidth="1px"
      borderColor="primary"
      borderLeftWidth="1px"
      borderRightWidth="1px"
      borderStyle="solid"
      bottomLeftRadius={isLast ? "sm" : undefined}
      bottomRightRadius={isLast ? "sm" : undefined}
      dir="column"
      gap="none"
      overflow="hidden"
    >
      <div className="border-l-4 border-l-amber-400 bg-amber-50">
        <Flex align="center" gap="md" overflow="hidden" p="xl">
          <UserImage
            alt={`${note.senderName} avatar`}
            imageUrl={null}
            name={note.senderName}
            size={UserImageSize.SMALL}
          />
          <Flex dir="column" gap="none" grow={1} minW="none">
            <Text
              fontSize="sm"
              fontWeight="semibold"
              textColor="primary"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {note.senderName}
            </Text>
          </Flex>
          <Text
            fontSize="xs"
            style={{ flexShrink: 0 }}
            textColor="tertiary"
            whiteSpace="nowrap"
          >
            {note.formattedTime}
          </Text>
        </Flex>
        <Flex dir="column" ml="4xl" p="lg" pt="none">
          {note.htmlContent ? (
            <div
              className="text-sm text-foreground"
              dangerouslySetInnerHTML={{ __html: note.htmlContent }}
            />
          ) : (
            <Text fontSize="sm" textColor="primary">
              {note.content || "(No content)"}
            </Text>
          )}
        </Flex>
      </div>
    </Flex>
  );
});
